import React, {ReactElement} from "react";
import {PathComponent} from "index";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../shared/BaseFragment";
import {gray, lightGray, translucentWhite} from "../shared/colors";
import {StyledBoxRow, StyledEditableTypography, StyledSpan} from "../shared/StyledComponents";
import {CARD_SHADOW_LARGE, DIVIDER, PD_LG, PD_MD, PD_XLG, SZ_SM} from "../shared/dimens";
import {Button, ButtonBase, Card, IconButton, Tooltip, useTheme} from "@mui/material";
import {
  AddOutlined,
  ArrowBackIosNewOutlined,
  ArrowForwardIosOutlined,
  LanguageOutlined,
  LinkOutlined,
  MoreVertOutlined,
  OpenInNewOutlined,
  PlayArrowOutlined,
  SaveOutlined
} from "@mui/icons-material";
import {Sidebar, SidebarPosition} from "./components/Sidebar";
import {PageView} from "@/smilebook-shared/components/PageView";
import {
  BG_COLORS,
  Book,
  EDITOR_TOOLS_KTM,
  EditorTool,
  Page,
  PAGE_LAYOUT_TEMPLATES,
  PageElement,
  PageElementType,
  Smilebook
} from "@/smilebook-shared/types";
import {SidebarContentBackground} from "./components/SidebarContentBackground";
import {MediaView} from "./components/MediaView";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE, DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN} from "../shared/BaseApp";
import {Preview} from "@/smilebook-shared/components/Preview";
import {SidebarContentLayout} from "./components/SidebarContentLayout";
import {SidebarContentText} from "./components/SidebarContentText";
import {SidebarContentImage} from "./components/SidebarContentImage";
import {Action, UserCache, UserProfilePhoto} from "../shared/types";
import {UserFragment} from "../shared/UserFragment";
import {App} from "./App";
import {getAuth} from "@firebase/auth";
import {User} from "../shared/entities";
import {LargeButton} from "./components/LargeButton";
import {sleep} from "../shared/sleep";
import {MediaImageUploadFragment} from "./components/MediaImageUploadFragment";
import {md5_uuid} from "../shared/md5";
import {PracticeSelect} from "../libwyze-shared/components/PracticeSelect";
import {AccountProfileContextTrait, Page as DentwyzePage, Pages} from "../libwyze-shared/types";
import {SidebarContentPreferences} from "./components/SidebarContentPreferences";

function MainContent(props: {
  book: Book,
  pageIndex: number,
  setPageIndex: (pageIndex: number) => void,
  selectedTool: EditorTool,
  setSelectedTool: (tool: EditorTool) => void,
  selectedElement: PageElement,
  setSelectedElement: (element: PageElement) => void,
  selectedImage: string,
  setSelectedImage: (image: string) => void,
}): ReactElement {
  const theme = useTheme();
  return <>
    <div style={{display: "flex",  flexGrow: 1, overflow: "hidden", position: "relative", background: gray}}>
      <div style={{display: "flex", width: "100%"}}>
        <Button
          disabled={props.pageIndex <= 0}
          onClick={() => props.setPageIndex(props.pageIndex - 1)}
          style={{minWidth: 0, width: 40}}>
          <ArrowBackIosNewOutlined/>
        </Button>
        <div style={{width: "100%", height: "100%", overflow: "scroll", display: "flex", alignItems:"center", justifyContent: "center"}}>
          <PageView
            style={{boxShadow: CARD_SHADOW_LARGE}}
            book={props.book}
            page={props.book.pages[props.pageIndex]}
            zoom={.84}
            selectedElement={props.selectedElement}
            setSelectedElement={props.setSelectedElement}
            selectedImage={props.selectedImage}
            setSelectedImage={props.setSelectedImage}/>
        </div>
        <Button
          disabled={props.pageIndex >= props.book.pages.length - 1}
          onClick={() => props.setPageIndex(props.pageIndex + 1)}
          style={{minWidth: 0, width: 40}}>
          <ArrowForwardIosOutlined/>
        </Button>
      </div>
      <div style={{
        width: 48,
        borderLeft: DIVIDER,
        display: "flex",
        flexDirection: 'column',
        background: theme.palette.background.default
      }}>
        {EDITOR_TOOLS_KTM.values.map(value => <Tooltip title={value.metadata.text} placement={"left"}>
          <Button
            onClick={() => {
              props.setSelectedTool(props.selectedTool === value.key ? null : value.key as EditorTool);
            }}
            style={{
              borderRadius: 0,
              borderRight: props.selectedTool === value.key ? "4px solid " + theme.palette.primary.main : null,
              minWidth: 0,
              width: 48,
              height: 72
            }}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "center"}}>
              <value.metadata.iconType/>
            </div>
          </Button>
        </Tooltip>)}
        <StyledSpan/>
      </div>
      {/*<Button*/}
      {/*  startIcon={<ViewCarouselOutlined/>}*/}
      {/*  endIcon={<KeyboardArrowUpOutlined/>}*/}
      {/*  style={{position: "absolute", left: 0, bottom: 0}}>*/}
      {/*  Edit Pages*/}
      {/*</Button>*/}
    </div>
    <div style={{
      display: "flex",
      height: 120,
      gap: 8,
      borderTop: DIVIDER,
      padding: PD_MD,
      flexShrink: 0,
      alignItems: "center",
      overflowX: "scroll"
    }}>
      {props.book.pages?.map((page, index) => <Button
        onClick={() => props.setPageIndex(index)}
        style={{
          flexShrink: 0,
          padding: 0,
          height: (120 - 2 * PD_MD),
          aspectRatio: 3 / 2,
          border: props.pageIndex === index ? "2px solid " + theme.palette.primary.main : null,
        }}>
        <PageView book={props.book} page={page} thumbnail={true}/>
      </Button>)}
    </div>
  </>;
}

export type MainProps = BaseFragmentProps & {}

type MainState = BaseFragmentState & {
  user?: User,
  practicePage?: DentwyzePage,
  selectedTool?: EditorTool,
  book: Book,
  pageIndex: number,
  selectedImage?: string,
  selectedElement?: PageElement,
  hasUnsavedDraft: boolean,
  hasUnpublishedDraft: boolean,
}

export class Main extends BaseFragment<MainProps, MainState> {

  private readonly auth = getAuth();

  constructor(props: MainProps, context: any) {
    super(props, context);
    const smilebook = Smilebook.getInstance();
    const draftVersion = smilebook?.draft();
    let book = draftVersion?.book;
    let hasUnpublishedDraft = book !== smilebook.published?.book;
    if (!book) {
      hasUnpublishedDraft = false;
      book = draftVersion.book = smilebook.published?.book || new Book();
      book.pages.push(...[
        // new Page().setLayoutTemplate(PAGE_LAYOUT_TEMPLATES.find(t => t.id === "image_1_1_ar_3_2")),
        // new Page().setLayoutTemplate(PAGE_LAYOUT_TEMPLATES[1]),
        // new Page(),
        // new Page().setLayoutTemplate(PAGE_LAYOUT_TEMPLATES[2]),
        // new Page().setLayoutTemplate(PAGE_LAYOUT_TEMPLATES[2]),
        // new Page(),
      ]);
    }
    this.state = {
      // selectedTool: EditorTool.TEXT,
      book: book,
      pageIndex: 0,
      hasUnsavedDraft: false,
      hasUnpublishedDraft,
    }
  }

  static nestedPaths(): PathComponent[] {
    return [];
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const user = await UserCache.getInstance().getUser(this.auth.currentUser.uid);
    const practicePageId = BaseApp.CONTEXT.getTrait<AccountProfileContextTrait>().getPracticePageId();
    const practicePage = await Pages.getInstance().getOrLoadItem(practicePageId);
    this.setState({
      user,
      practicePage,
    });
  }

  componentDidUpdate(prevProps: Readonly<MainProps>, prevState: Readonly<MainState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.pageIndex !== this.state.pageIndex) {
      if (this.state.selectedElement) {
        this.setState({
          selectedElement: null,
        });
      }
    }
    if (prevState.selectedElement !== this.state.selectedElement) {
      this.setState({
        hasUnsavedDraft: true,
        hasUnpublishedDraft: true,
      });
    }
  }

  private getSmilebookLink(): string {
    return `https://smilebook.dentwyze.in/?id=${Smilebook.getInstance().practiceId}`;
  }

  protected renderContainerContent(): React.ReactElement | null {
    const book = this.state.book;
    const pageIndex = this.state.pageIndex;
    const page = book.pages[pageIndex];
    const selectedElement = this.state.selectedElement;
    const selectedBlock = selectedElement?.blockIndex !== undefined ? selectedElement.page.layout.block(selectedElement.blockIndex) : undefined;
    return <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100dvw",
        height: "100dvh",
        background: lightGray,
        overflow: "hidden",
      }}>
      <StyledBoxRow style={{
        position: "relative",
        zIndex: 1,
        background: translucentWhite,
        width: "100%",
        alignItems: "center",
        padding: `0px ${PD_XLG}px`,
        height: 72,
        flexShrink: 0,
        borderBottom: DIVIDER,
      }}>
        <img src={"/images/dentwyze_logotype.svg"} style={{height: 32}}/>
        <img src={"/images/dentwyze_smilebook_logotype.svg"} style={{height: 40, marginRight: PD_LG}}/>
        <div style={{width: 400}}>
          <StyledEditableTypography
            placeholder={"Book Title"}
            onTextInputConfirmed={(e, text) => {
              book.title = text;
              this.setState({
                hasUnsavedDraft: false,
              });
              this.saveDraft();
            }}
            style={{width: "100%", textAlign: "start"}}
            variant={"h6"}>
            {book.title}
          </StyledEditableTypography>
        </div>
        <StyledSpan/>
        <Tooltip title={"Save"}>
          <IconButton
            disabled={!this.state.hasUnsavedDraft}
            onClick={() => this.saveDraft()}>
            <SaveOutlined/>
          </IconButton>
        </Tooltip>
        <Tooltip title={"Preview"}>
          <IconButton
            onClick={() => {
              BaseApp.CONTEXT.showDialog(
                {flags: DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN | DIALOG_FLAG_SHOW_CLOSE},
                () => <Preview book={book}/>);
            }}>
            <PlayArrowOutlined/>
          </IconButton>
        </Tooltip>
        <LargeButton
          disabled={!this.state.hasUnpublishedDraft}
          onClick={() => this.publish()}
          color={"secondary"}
          startIcon={<LanguageOutlined/>}
          endIcon={<ArrowForwardIosOutlined/>}
          variant={"contained"}>
          Save & Publish
        </LargeButton>
        <IconButton onClick={(e) => {
          BaseApp.CONTEXT.showActionsListPopover(e.target as HTMLElement, {
            anchorOrigin: {
              horizontal: "center",
              vertical: "bottom"
            }
          }, [
            new Action("View Smilebook", () => {
              window.open(this.getSmilebookLink(), "_blank");
            }, OpenInNewOutlined),
            new Action("Copy Link", () => {
              window.navigator.clipboard.writeText(this.getSmilebookLink())
                .then(() => BaseApp.CONTEXT.showToast("Copied link!"));
            }, LinkOutlined),
          ]);
        }}>
          <MoreVertOutlined/>
        </IconButton>
        <PracticeSelect
          practices={[this.state.practicePage.practicePageProfile]}
          selectedPractice={this.state.practicePage.practicePageProfile}
          setSelectedPractice={null}
          linkPractice={null}/>
        <Card style={{marginLeft: PD_LG, width: SZ_SM, height: SZ_SM, flexShrink: 0}}>
          <ButtonBase
            onClick={() => App.CONTEXT.showDialog(
              null,
              () => <UserFragment uid={this.auth.currentUser.uid}/>)}
          >
            <img src={UserProfilePhoto(this.state.user)} style={{width: "100%", height: "100%"}}/>
          </ButtonBase>
        </Card>
      </StyledBoxRow>
      <div style={{display: "flex", flexGrow: 1, height: "calc(100dvh-72px)", overflow: "hidden"}}>
        <Sidebar
          sidebarCollapsible={true}
          toolbar={<StyledBoxRow>
            <Button
              onClick={() => {
                BaseApp.CONTEXT.showDialog(null, () => <MediaImageUploadFragment
                  value={null}
                  setValue={(value, file) => {
                    book.media.images.push({
                      id: md5_uuid(),
                      name: file?.file.name,
                      url: value,
                    });
                    this.saveDraft();
                  }}/>);
              }}
              startIcon={<AddOutlined/>}>
              Upload
            </Button>
          </StyledBoxRow>}
          title={"Media"} position={SidebarPosition.LEFT}>
          <MediaView
            media={book.media}
            selectedImage={this.state.selectedImage}
            setSelectedImage={id => this.setState({selectedImage: id})}
          />
        </Sidebar>
        <div key={`main-${this.state.selectedTool ? "sel" : "none"}`}
             style={{display: "flex", flexDirection: "column", width: 0, flexGrow: 1}}>
          <MainContent
            book={book}
            pageIndex={pageIndex}
            setPageIndex={pageIndex => this.setState({pageIndex})}
            selectedTool={this.state.selectedTool}
            setSelectedTool={tool => this.setState({selectedTool: tool})}
            selectedElement={this.state.selectedElement}
            setSelectedElement={element => {
              this.setState({selectedElement: element});
              switch (element?.type) {
                case PageElementType.IMAGE:
                  this.setState({selectedTool: EditorTool.IMAGE});
                  break;
                case PageElementType.TEXT:
                case PageElementType.SUBTEXT:
                  this.setState({selectedTool: EditorTool.TEXT});
                  break;
                case PageElementType.BACKGROUND:
                  this.setState({selectedTool: EditorTool.BACKGROUND});
                  break;
              }
            }}
            selectedImage={this.state.selectedImage}
            setSelectedImage={id => this.setState({selectedImage: id})}/>
        </div>
        {Boolean(this.state.selectedTool)
          ? <Sidebar
            title={EDITOR_TOOLS_KTM.findMetadata(this.state.selectedTool).text}
            position={SidebarPosition.RIGHT}>
            {this.state.selectedTool === EditorTool.PREFERENCES
              ? <SidebarContentPreferences
                selectedElement={this.state.selectedElement}
                selectedPreferences={this.state.book.preferences}
                setSelectedPreferences={preferences => {
                  this.state.book.preferences = preferences;
                  this.forceUpdate();
                }}
              />
              : null}
            {this.state.selectedTool === EditorTool.IMAGE
              ? <SidebarContentImage
                selectedElement={this.state.selectedElement}
                selectedImageAttributes={selectedBlock?.imageAttributes}
                setSelectedImageAttributes={attributes => {
                  selectedBlock.imageAttributes = attributes;
                  this.forceUpdate();
                }}
              />
              : null}
            {this.state.selectedTool === EditorTool.TEXT
              ? <SidebarContentText
                selectedElement={selectedElement}
                selectedTextAttributes={selectedBlock?.textAttributes}
                setSelectedTextAttributes={attributes => {
                  selectedBlock.textAttributes = attributes;
                  this.forceUpdate();
                }}
              />
              : null}
            {this.state.selectedTool === EditorTool.BACKGROUND
              ? <SidebarContentBackground
                selectedElement={this.state.selectedElement}
                colors={BG_COLORS}
                selectedColor={page.background}
                setSelectedColor={color => {
                  page.background = color;
                  this.forceUpdate();
                }}/>
              : null}
            {this.state.selectedTool === EditorTool.LAYOUT
              ? <SidebarContentLayout
                selectedElement={this.state.selectedElement}
                templates={PAGE_LAYOUT_TEMPLATES}
                selectedLayoutTemplate={page.layout.id}
                setSelectedLayoutTemplate={id => {
                  page.setLayoutTemplate(PAGE_LAYOUT_TEMPLATES.find(t => t.id === id));
                  this.forceUpdate();
                }}/>
              : null}
          </Sidebar>
          : null}
      </div>
    </div>;
  }

  private async saveDraft(): Promise<void> {
    const main = this;
    BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
      await sleep(Math.random() * 1000);
      await Smilebook.getInstance().save();
      main.setState({
        hasUnsavedDraft: false,
      });
    });
  }

  private async publish(): Promise<void> {
    const main = this;
    BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
      await sleep(Math.random() * 1000);
      await Smilebook.getInstance().publish();
      main.setState({
        hasUnsavedDraft: false,
        hasUnpublishedDraft: false,
      });
    });
  }
}
